/**
 * Analytics Module responsible for tracking user interactions
 * The interface for tracking events is based on data attributes
 * being added to the DOM element using the naming convention:
 * Enable Analytics:
 * data-analytics="true"
 *
 * Event Name:
 * data-analytics_event="{event name}"
 *
 * Event Parameters:
 * data-analytics_{key}="{value}"
 * key: the name of the parameter to set in the dataLayer object
 * value: the value to set for the key
 *
 * To debug analytics, add ?debugAnalytics=true to the URL. This will log the updated object pushed to window.dataLayer
 *
 * @example
 * <a href="https://www.masonite.com" data-analytics data-analytics_event="click" data-analytics_click_url="https://www.google.com">Masonite</a>
 *
 * // will result in the following object being pushed to the window.dataLayer array
 * {
 *    event: 'click',
 *    click_url: 'https://www.google.com'
 * }
 *
 */
export default class Analytics {
	/**
	 * Returns the singleton instance of the Analytics module
	 * @returns {Analytics}
	 */
	static getInstance() {
		if (!this.instance) {
			this.instance = new Analytics()
		}
		return this.instance
	}

	constructor(element) {
		this.datasetPrefix = 'analytics_'
		this.isBrowser = typeof window !== 'undefined'

		this.attachEvents()
	}

	/**
	 * Attaches event listeners to the DOM element to listen for click events on all document child elements
	 */
	attachEvents() {
		document.addEventListener('click', this.handleClick)
		document.addEventListener('change', this.handleInputChange)
	}

	/**
	 * Detaches event listeners from the DOM element
	 */
	detachEvents() {
		document.removeEventListener('click', this.handleClick)
		document.removeEventListener('change', this.handleInputChange)
	}

	/**
	 * Destroys the module
	 */
	destroy() {
		this.detachEvents()
	}

	/**
	 * Callback for click events on the document that will find the closet parent element with the data-analytics attribute
	 * and trigger the analytics event
	 * @param {*} event
	 */
	handleClick = (event) => {
		const closestTarget = event.target.closest('[data-analytics]')
		const ignoreElement = ['INPUT', 'SELECT', 'TEXTAREA'] // ignore these elements when clicking on them

		if (closestTarget && !ignoreElement.includes(closestTarget?.tagName)) {
			this.triggerAnalytics(closestTarget)
		}
	}

	/**
	 * Callback for change events on the document that will find the closet parent element with the data-analytics attribute
	 * and trigger the analytics event.
	 * Note: this is currently only needed for select elements and can be enhanced to support other elements in the future
	 * @param {*} event
	 */
	handleInputChange = (event) => {
		const closestTarget = event.target.closest('[data-analytics]')
		if (closestTarget) {
			// if the closetTarget is a select element, grab the selection option
			// and loop through the attributes to find the data-analytics attributes
			// and set them on the select element
			if (closestTarget.tagName === 'SELECT') {
				const selectedOption =
					closestTarget.options[closestTarget.selectedIndex]

				if (selectedOption.attributes) {
					Array.from(selectedOption.attributes).forEach((attribute) => {
						if (attribute.name.startsWith(`data-${this.datasetPrefix}`)) {
							closestTarget.setAttribute(attribute.name, attribute.value)
						}
					})
				}
			}

			this.triggerAnalytics(closestTarget)
		}
	}

	/**
	 * Triggers the analytics event based on the data-analytics attributes on the target element
	 * @param {Element} target - The DOM element to trigger the analytics event on
	 */
	triggerAnalytics = (target) => {
		const dataset = target.dataset
		const params = {}
		let event = ''

		// Loop through all data-analytics attributes and add them to the params object
		Object.keys(dataset).forEach((key) => {
			if (key.startsWith(this.datasetPrefix)) {
				const value = dataset[key]
				const paramKey = key.replace(this.datasetPrefix, '')

				if (paramKey === 'event') {
					event = value
				} else {
					params[paramKey] = value
				}
			}
		})

		if (target.href) {
			params.click_url = target.href
		}

		if (event && Object.keys(params).length > 0) {
			this.trackInteraction(event, params)
		}
	}

	/**
	 * Tracks the interaction by pushing the event and params to the window.dataLayer array
	 * @param {String} event - The event name
	 * @param {Object} params - The event parameters
	 */
	trackInteraction = (event, params = {}) => {
		if (this.isBrowser && window?.dataLayer) {
			const dlObj = {
				event,
				...params
			}

			window.dataLayer.push(dlObj)

			if (this.isDebuggingAnalytics()) {
				console.log(
					'Analytics logging updated object pushed to window.dataLayer'
				)
				console.log(dlObj)
			}
		}
	}

	/**
	 * Checks if the debugAnalytics query param is set
	 * @returns {Boolean}
	 */
	isDebuggingAnalytics = () => {
		if (!this.isBrowser) {
			return false
		}
		const params = new URLSearchParams(window.location.search)
		return params.get('debugAnalytics') !== null
	}
}
